import { useEffect, useState } from 'react'

/** One toggleable column, as the caller declares it. */
export interface ColumnVisibilityOption {
  /** Must match the key used when hiding the column. */
  colId: string
  /** Label shown in the chooser. */
  headerName: string
}

/** One toggleable column, as the chooser renders it. */
export interface ColumnChooserColumn extends ColumnVisibilityOption {
  visible: boolean
}

export interface UseColumnVisibilityArgs {
  /**
   * The full set of toggleable columns, in display order. Identity and action
   * columns simply stay out of this list.
   */
  columns: ColumnVisibilityOption[]
  /**
   * localStorage key. Omit to keep the choice for the current page session
   * only — nothing is read or written.
   */
  storageKey?: string
}

export interface UseColumnVisibilityResult {
  /** Every column with its current visibility, for the chooser UI. */
  columns: ColumnChooserColumn[]
  /** Feed into antd's `hidden` on a column def, or a render guard. */
  isHidden: (colId: string) => boolean
  /** Inverse of `isHidden`, for readability at JSX call sites. */
  isVisible: (colId: string) => boolean
  setColumnVisible: (colId: string, visible: boolean) => void
  /** Back to all-visible. */
  resetColumns: () => void
}

/**
 * Reads the persisted hidden set, dropping anything the caller no longer
 * declares.
 *
 * Storing the HIDDEN ids (rather than a full visibility map) is what makes a
 * changing column set safe in both directions: a column added later is absent
 * from the list and so defaults to visible, and a column removed later is
 * filtered out here instead of lingering as a key that hides nothing. Without
 * that filter a renamed colId would sit in storage forever.
 */
const readHiddenIds = (
  storageKey: string | undefined,
  columns: ColumnVisibilityOption[]
): string[] => {
  if (!storageKey || typeof window === 'undefined') {
    return []
  }

  try {
    const raw = window.localStorage.getItem(storageKey)
    if (!raw) {
      return []
    }
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) {
      return []
    }
    const known = new Set(columns.map((column) => column.colId))
    return parsed.filter(
      (id): id is string => typeof id === 'string' && known.has(id)
    )
  } catch {
    // Unreadable or corrupt storage is not worth surfacing — a private window
    // and a hand-edited value both land here, and the right answer for both is
    // the default layout.
    return []
  }
}

const writeHiddenIds = (storageKey: string | undefined, hidden: string[]) => {
  if (!storageKey || typeof window === 'undefined') {
    return
  }
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(hidden))
  } catch {
    // Quota or a blocked store — the session still works, it just will not be
    // remembered. Failing the toggle would be worse than forgetting it.
  }
}

/**
 * Column show/hide state for one set of columns, optionally remembered.
 *
 * Holds STATE rather than driving a table instance: the registration page
 * renders one table per division plus one per expanded team, and a single
 * chooser has to reach all of them. Consumers read `isHidden` and apply it
 * themselves — antd's `hidden` on a column def, or a render guard for the
 * division header, which has no column defs.
 *
 * @example
 * const teamColumns = useColumnVisibility({
 *   columns: TEAM_CHOOSER_COLUMNS,
 *   storageKey: 'sn.registration.columns.teams',
 * })
 * // in a column def:  hidden: teamColumns.isHidden('paid')
 */
export const useColumnVisibility = ({
  columns,
  storageKey,
}: UseColumnVisibilityArgs): UseColumnVisibilityResult => {
  'use memo'

  const [hiddenIds, setHiddenIds] = useState<string[]>(() =>
    readHiddenIds(storageKey, columns)
  )

  // The initial read happens in the state initializer; this only has to cover
  // a caller that swaps storageKey.
  useEffect(() => {
    setHiddenIds(readHiddenIds(storageKey, columns))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey])

  const hidden = new Set(hiddenIds)

  const persist = (next: string[]) => {
    setHiddenIds(next)
    writeHiddenIds(storageKey, next)
  }

  const setColumnVisible = (colId: string, visible: boolean) => {
    if (visible) {
      if (!hidden.has(colId)) {
        return
      }
      persist(hiddenIds.filter((id) => id !== colId))
      return
    }

    if (hidden.has(colId)) {
      return
    }
    persist([...hiddenIds, colId])
  }

  const resetColumns = () => {
    persist([])
  }

  return {
    columns: columns.map((column) => ({
      ...column,
      visible: !hidden.has(column.colId),
    })),
    isHidden: (colId: string) => hidden.has(colId),
    isVisible: (colId: string) => !hidden.has(colId),
    setColumnVisible,
    resetColumns,
  }
}

export default useColumnVisibility
